import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import {
  MenuItem,
  TextField,
  Autocomplete,
  IconButton,
  Button,
  Box,
  Avatar,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import CheckIcon from "@mui/icons-material/Check";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { GetCustomersList } from "api/customers";
import { GetRouteList } from "api/routes";
import { StorePackage } from "api/packages";
import { GetStationDetailById } from "api/stations";
import { StoreTrackingLog } from "api/trackingLogs";
import { user } from "localStorage";
import { getScanText, playCorrectSound, playIncorrectSound, selectAndFocus, TRACKING_MAX_LENGTH } from "utils";
import Scanner from "components/Scanner";
import { GetAgentList } from "api/agent";
import BentoCard from "components/bento/BentoCard";
import { sectionLabelSx, fieldSx, accentFieldSx, FieldLabel, ToggleCard } from "components/bento/BentoFormControls";
import {
  BG_TINT,
  BORDER,
  BORDER_PURPLE,
  BG_PURPLE_TINT,
  RADIUS_INPUT,
  DIVIDER,
  PURPLE_MAIN,
  PURPLE_DARK,
  TEXT_BODY,
  MONO,
} from "components/bento/bentoTokens";

const DRAFT_KEY = "newPackageDraft";

const initials = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts.slice(0, 2).map((p) => p[0]).join("").toUpperCase();
};

const NewPackage = () => {
  const { t } = useTranslation();
  // Data to send to back-end
  const [tracking, setTracking] = useState("");
  const [description, setDescription] = useState("");
  const [orderId, setOrderId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [routeId, setRouteId] = useState("");
  const [customer, setCustomer] = useState(null);
  const [shippingFee, setShippingFee] = useState(0);
  const [open, setOpen] = useState(false);
  const [isDeliveryRequired, setIsDeliveryRequired] = useState(false);
  const [isCOD, setIsCOD] = useState(false);
  const [isSubCustomer, setIsSubCustomer] = useState(false);
  const [level, setLevel] = useState("1");
  const [agentList, setAgentList] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [isForwardRequired, setIsForwardRequired] = useState(false);
  const [isSelfOrder, setIsSelfOrder] = useState(false);
  const [transitCheckRequired, setTransitCheckRequired] = useState(false);
  const [finalCheckRequired, setFinalCheckRequired] = useState(false);
  const [noTrackingYet, setNoTrackingYet] = useState(false);
  const [deliveryAddresses, setDeliveryAddresses] = useState([]);
  const [forwardAddresses, setForwardAddresses] = useState([]);
  const [deliveryAddress, setDeliveryAddress] = useState(null);
  const [forwardAddress, setForwardAddress] = useState(null);

  const status = "Pending";

  const [remark, setRemark] = useState("");
  const [amount, setAmount] = useState(0);
  const paymentStatus = false;

  // Data for front-end
  const [searchCustomer, setSearchCustomer] = useState("");
  const [customerList, setCustomerList] = useState([]);
  const [routeList, setRouteList] = useState([]);

  const _user = user();
  const isAgentUser = _user?.role === "agent";

  const trackingError = !noTrackingYet && Boolean(tracking) && tracking.length > 0 && tracking.length < 8;

  const clearForm = () => {
    setTracking("");
    setDescription("");
    setOrderId("");
    setAmount(0);
    setQuantity(1);
    setShippingFee(0);
    setRemark("");
    setTransitCheckRequired(false);
    setFinalCheckRequired(false);
    setNoTrackingYet(false);
    localStorage.removeItem(DRAFT_KEY);
    selectAndFocus("tracking");
    // Note: customer-related fields are cleared separately when customer is cleared
    // This allows for partial form clearing while keeping customer context
  };

  const handleSaveDraft = () => {
    localStorage.setItem(
      DRAFT_KEY,
      JSON.stringify({ tracking, description, orderId, remark, amount, quantity, shippingFee, customer })
    );
    Swal.fire({ title: "Draft saved", icon: "success", timer: 1200, showConfirmButton: false });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    if (!customer) {
      Swal.fire({
        title: "Error!",
        icon: "error",
        text: "Please select a customer",
        showConfirmButton: true,
      });
      return;
    }

    if (!noTrackingYet) {
      if (!tracking || tracking.trim() === "") {
        Swal.fire({
          title: "Error!",
          icon: "error",
          text: "Please enter a tracking number",
          showConfirmButton: true,
        });
        return;
      }

      // Validate tracking number length
      if (tracking && tracking.length < 8) {
        Swal.fire({
          title: "Tracking Number Too Short!",
          icon: "error",
          html: `<div style="color:#E53E3E; font-size: 16px; margin-bottom:8px;">${tracking}</div>
                 <div>Tracking number must be at least 8 characters long.<br/>
                 Current length: ${tracking.length} characters</div>`,
          showConfirmButton: true,
        });
        return;
      }
    }

    if (!routeId) {
      Swal.fire({
        title: "Error!",
        icon: "error",
        text: "Please select a route",
        showConfirmButton: true,
      });
      return;
    }

    // Validate conditional required fields
    if (isDeliveryRequired && !deliveryAddress) {
      Swal.fire({
        title: "Error!",
        icon: "error",
        text: "Please select a delivery address",
        showConfirmButton: true,
      });
      return;
    }

    if (isForwardRequired && !forwardAddress) {
      Swal.fire({
        title: "Error!",
        icon: "error",
        text: "Please select a forward address",
        showConfirmButton: true,
      });
      return;
    }

    if (isSubCustomer && !agentId) {
      Swal.fire({
        title: "Error!",
        icon: "error",
        text: "Please select an agent for sub-customer",
        showConfirmButton: true,
      });
      return;
    }

    StorePackage(
      tracking,
      description,
      orderId,
      customer._id,
      amount,
      quantity,
      shippingFee,
      routeId,
      status,
      remark,
      paymentStatus,
      "STD",
      false,
      isCOD,
      isDeliveryRequired,
      isForwardRequired,
      isSubCustomer, // isSubCustomer field
      agentId, // agentId field
      level,
      isSelfOrder,
      forwardAddress,
      deliveryAddress,
      undefined, // finalShippingFee — not exposed on this form; server derives it for agent sub-customers
      transitCheckRequired,
      finalCheckRequired,
      noTrackingYet
    ).then((res) => {
      console.log(res);
      if (res.success || res.status === 201) {
        const data = res.data;
        clearForm();
        // setCustomer(null); // Don't clear customer selection
        playCorrectSound();
        let account = user()?.name || "Unknown User";
        GetStationDetailById("STD").then((stationRes) => {
          if (stationRes.success || stationRes.status === 200) {
            StoreTrackingLog(
              data._id,
              data.tracking,
              stationRes.data.name,
              stationRes.data.message,
              data.remark,
              account
            ).then((logRes) => {
              // console.log(logRes);
              if (logRes.success || logRes.status === 200) {
                console.log(`Trackinglog has been saved for started station.`);
              } else {
                console.error("Tracking log save failed:", logRes.message);
                Swal.fire({
                  title: "Warning!",
                  icon: "warning",
                  text: `Package created successfully, but tracking log save failed. Please contact developer.`,
                  showConfirmButton: true,
                });
              }
            }).catch((logError) => {
              console.error("Tracking log error:", logError);
              Swal.fire({
                title: "Warning!",
                icon: "warning",
                text: `Package created successfully, but tracking log failed due to network error.`,
                showConfirmButton: true,
              });
            });
          } else {
            console.error("Station details fetch failed:", stationRes.message);
          }
        }).catch((stationError) => {
          console.error("Station details error:", stationError);
        });

        Swal.fire({
          title: "Created!",
          icon: "success",
          text: res.message || `${tracking} has been created`,
          timer: 1500,
        });
      } else {
        playIncorrectSound();
        Swal.fire({
          title: "Error!",
          icon: "error",
          text: res.message || "Failed to create package",
          showConfirmButton: true,
        });
      }
    }).catch((error) => {
      console.error("Package creation error:", error);
      playIncorrectSound();
      Swal.fire({
        title: "Network Error!",
        icon: "error",
        text: "Failed to create package due to network error. Please check your connection and try again.",
        showConfirmButton: true,
      });
    });
  };

  useEffect(() => {
    const saved = localStorage.getItem(DRAFT_KEY);
    if (!saved) return;
    let draft;
    try {
      draft = JSON.parse(saved);
    } catch {
      localStorage.removeItem(DRAFT_KEY);
      return;
    }
    Swal.fire({
      title: "Resume saved draft?",
      text: "A package draft was found from your last session.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Resume",
      cancelButtonText: "Discard",
    }).then((result) => {
      if (result.isConfirmed) {
        setTracking(draft.tracking || "");
        setDescription(draft.description || "");
        setOrderId(draft.orderId || "");
        setRemark(draft.remark || "");
        setAmount(draft.amount ?? 0);
        setQuantity(draft.quantity ?? 1);
        setShippingFee(draft.shippingFee ?? 0);
        // Restoring `customer` re-triggers the normal customer-selection effect below,
        // which re-derives route/toggles/addresses from that customer — same as a fresh pick.
        if (draft.customer) setCustomer(draft.customer);
      } else {
        localStorage.removeItem(DRAFT_KEY);
      }
    });
  }, []);

  useEffect(() => {
    setSearchCustomer("");
    GetRouteList().then((res) => {
      if (res.success && res.data) {
        setRouteList(res.data);
      } else {
        console.error("Failed to load routes:", res.message);
        // Optionally show user-friendly error
        if (!res.success) {
          Swal.fire({
            title: "Warning",
            icon: "warning",
            text: "Failed to load routes. Please refresh the page.",
            showConfirmButton: true,
          });
        }
      }
    }).catch((error) => {
      console.error("Error loading routes:", error);
      Swal.fire({
        title: "Error",
        icon: "error",
        text: "Network error loading routes. Please check your connection.",
        showConfirmButton: true,
      });
    });
  }, []);

  useEffect(() => {
    GetCustomersList(searchCustomer).then((res) => {
      console.log("Customer API response:", res); // Debug log
      if (res.success && res.data) {
        console.log("Setting customer list:", res.data); // Debug log
        setCustomerList(res.data);
      } else {
        console.log("Customer API failed:", res.message);
        setCustomerList([]); // Reset to empty array on failure
      }
    }).catch((error) => {
      console.error("Error loading customers:", error);
      setCustomerList([]); // Reset to empty array on error
    });
  }, [searchCustomer]);

  useEffect(() => {
    if (customer) {
      "default_route" in customer
        ? setRouteId(customer.default_route)
        : setRouteId("");
      "forwardAddresses" in customer
        ? setForwardAddresses(customer.forwardAddresses)
        : setForwardAddresses([]);
      "deliveryAddresses" in customer
        ? setDeliveryAddresses(customer.deliveryAddresses)
        : setDeliveryAddresses([]);
      "isDeliveryRequired" in customer
        ? setIsDeliveryRequired(customer.isDeliveryRequired)
        : setIsDeliveryRequired(false);
      "isForwardRequired" in customer
        ? setIsForwardRequired(customer.isForwardRequired)
        : setIsForwardRequired(false);
      "isSelfOrder" in customer
        ? setIsSelfOrder(customer.isSelfOrder)
        : setIsSelfOrder(false);
      "isSubCustomer" in customer
        ? setIsSubCustomer(customer.isSubCustomer)
        : setIsSubCustomer(false);
      "agentId" in customer ? setAgentId(customer.agentId) : setAgentId("");
      "level" in customer ? setLevel(customer.level) : setLevel("1");
      "isCOD" in customer ? setIsCOD(customer.isCOD) : setIsCOD(false);
    }
  }, [customer]);

  useEffect(() => {
    // Find the default address
    const defaultAddress = deliveryAddresses.find(
      (address) => address.isDefault === true
    );
    if (defaultAddress) {
      setDeliveryAddress(defaultAddress);
    } else {
      setDeliveryAddress(null);
    }
  }, [deliveryAddresses]); // Run effect when deliveryAddresses change

  useEffect(() => {
    // Find the default address
    const defaultAddress = forwardAddresses.find(
      (address) => address.isDefault === true
    );
    if (defaultAddress) {
      setForwardAddress(defaultAddress);
    } else {
      setForwardAddress(null);
    }
  }, [forwardAddresses]); // Run effect when forwardAddresses change

  useEffect(() => {
    if (isSubCustomer) {
      GetAgentList().then((res) => {
        if (res.success && res.data) {
          setAgentList(res.data);
        } else {
          console.error("Failed to load agent list:", res.message);
          setAgentList([]); // Reset to empty array
        }
      }).catch((error) => {
        console.error("Error loading agent list:", error);
        setAgentList([]); // Reset to empty array
      });
    } else {
      setAgentId("");
      setAgentList([]); // Clear agent list when not needed
    }
  }, [isSubCustomer]);

  return (
    <form onSubmit={handleFormSubmit}>
      <BentoCard sx={{ maxWidth: "860px" }} bodySx={{ p: 0 }}>

        {/* Section 1: Customer & Route */}
        <Box sx={{ p: "24px 28px 20px", borderBottom: `1px solid ${DIVIDER}` }}>
          <Box sx={sectionLabelSx}>Customer &amp; Route</Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "16px", mb: "16px" }}>
            <Autocomplete
              onInputChange={(event, newInputValue) => {
                setSearchCustomer(newInputValue);
              }}
              onChange={(e, value) => {
                if (value) {
                  setCustomer(value);
                } else {
                  // Handle case when user clears selection
                  setCustomer(null);
                  setRouteId("");
                  setDeliveryAddresses([]);
                  setForwardAddresses([]);
                  setDeliveryAddress(null);
                  setForwardAddress(null);
                  setIsDeliveryRequired(false);
                  setIsForwardRequired(false);
                  setIsSelfOrder(false);
                  setIsSubCustomer(false);
                  setAgentId("");
                  setLevel("1");
                  setIsCOD(false);
                  setTransitCheckRequired(false);
                  setFinalCheckRequired(false);
                }
              }}
              disablePortal
              id="combo-box-customer"
              options={customerList}
              value={customer} // Controlled component
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => {
                if (!option || !option.name) return "";
                return option.name + " - " + (option.whatsapp || "");
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: "11px",
                      fontWeight: 700,
                      background: "linear-gradient(135deg,#7c3aed,#4c1d95)",
                    }}
                  >
                    {initials(option.name)}
                  </Avatar>
                  <Box sx={{ fontSize: "13.5px", fontWeight: 600, color: TEXT_BODY }}>
                    {option.name}{option.whatsapp ? ` · ${option.whatsapp}` : ""}
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Customer"
                  required
                  sx={fieldSx}
                  error={!customer && params.inputProps.value !== ""}
                  helperText={!customer && params.inputProps.value !== "" ? "Please select a customer" : ""}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: customer ? (
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          fontSize: "11px",
                          fontWeight: 700,
                          background: "linear-gradient(135deg,#7c3aed,#4c1d95)",
                          mr: "2px",
                        }}
                      >
                        {initials(customer.name)}
                      </Avatar>
                    ) : params.InputProps.startAdornment,
                  }}
                />
              )}
            />

            <TextField
              select
              label="Route"
              id="route"
              disabled={!customer}
              value={!routeId ? "" : routeId}
              onChange={(event) => setRouteId(event.target.value)}
              required
              sx={fieldSx}
            >
              {routeList &&
                routeList.map(({ id, name }) => (
                  <MenuItem key={id} value={id}>
                    {id} ({name})
                  </MenuItem>
                ))}
            </TextField>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: "12px" }}>
            <ToggleCard
              label="COD"
              sub="ເກັບຄ່າບໍລິການປາຍທາງ"
              checked={isCOD}
              disabled={!customer}
              onChange={() => setIsCOD(!isCOD)}
            />

            <ToggleCard
              label="ລູກຄ້າສັ່ງເຄື່ອງເອງ"
              sub="Self-order"
              checked={isSelfOrder}
              disabled={!customer}
              onChange={() => setIsSelfOrder(!isSelfOrder)}
            />

            {!isAgentUser && (
              <ToggleCard
                label="ລູກຄ້າຂອງຕົວແທນ"
                sub="Sub-customer order"
                checked={isSubCustomer}
                disabled={!customer}
                onChange={() => setIsSubCustomer(!isSubCustomer)}
              >
                {isSubCustomer && (
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="ເລືອກຕົວແທນ Agent"
                    value={!agentId ? "" : agentId}
                    required
                    sx={accentFieldSx}
                    onChange={(event) => setAgentId(event.target.value)}
                  >
                    {agentList &&
                      agentList.map(({ id, name }) => (
                        <MenuItem key={id} value={id}>
                          {id} - {name}
                        </MenuItem>
                      ))}
                  </TextField>
                )}
              </ToggleCard>
            )}
          </Box>
        </Box>

        {/* Section 2: Delivery Options */}
        <Box sx={{ p: "22px 28px 20px", borderBottom: `1px solid ${DIVIDER}` }}>
          <Box sx={sectionLabelSx}>Delivery Options</Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "12px" }}>

            <ToggleCard
              label="Home Delivery"
              sub="ຈັດສົ່ງສິນຄ້າຮອດເຮືອນລູກຄ້າ"
              checked={isDeliveryRequired}
              disabled={!customer}
              onChange={() => setIsDeliveryRequired(!isDeliveryRequired)}
            >
              {isDeliveryRequired && (
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="ເລືອກທີ່ຢູ່"
                  value={deliveryAddress?.id || ""}
                  required
                  sx={accentFieldSx}
                  onChange={(event) => {
                    const selectedAddress = deliveryAddresses.find(
                      (addr) => addr.id === event.target.value
                    );
                    setDeliveryAddress(selectedAddress || null);
                  }}
                >
                  {deliveryAddresses &&
                    deliveryAddresses.map((address) => (
                      <MenuItem key={address.id} value={address.id}>
                        {address.title} - {address.description}
                      </MenuItem>
                    ))}
                </TextField>
              )}
            </ToggleCard>

            <ToggleCard
              label="ຝາກນຳຂົນສົ່ງຕໍ່"
              sub="ເຊັ່ນ ຮຸ່ງອາລຸນ, ອະນຸສິດ, ມີໄຊ ..."
              checked={isForwardRequired}
              disabled={!customer}
              onChange={() => setIsForwardRequired(!isForwardRequired)}
            >
              {isForwardRequired && (
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="ເລືອກທີ່ຢູ່"
                  value={forwardAddress?.id || ""}
                  required
                  sx={accentFieldSx}
                  onChange={(event) => {
                    const selectedAddress = forwardAddresses.find(
                      (addr) => addr.id === event.target.value
                    );
                    setForwardAddress(selectedAddress || null);
                  }}
                >
                  {forwardAddresses &&
                    forwardAddresses.map((address) => (
                      <MenuItem key={address.id} value={address.id}>
                        {address.logisticCompany} - {address.branch} {address.description}
                      </MenuItem>
                    ))}
                </TextField>
              )}
            </ToggleCard>
          </Box>
        </Box>

        {/* Section 3: Fragile Check */}
        <Box sx={{ p: "22px 28px 20px", borderBottom: `1px solid ${DIVIDER}` }}>
          <Box sx={sectionLabelSx}>Fragile Check</Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "12px" }}>
            <ToggleCard
              label="Fragile check — Transit"
              sub="ຕ້ອງແນບຮູບກ່ອນສະແກນຜ່ານສະຖານີປ່ຽນຖ່າຍ"
              checked={transitCheckRequired}
              disabled={!customer}
              onChange={() => setTransitCheckRequired(!transitCheckRequired)}
            />
            <ToggleCard
              label="Fragile check — Final station"
              sub="ຕ້ອງແນບຮູບກ່ອນສະແກນຜ່ານສະຖານີປາຍທາງ"
              checked={finalCheckRequired}
              disabled={!customer}
              onChange={() => setFinalCheckRequired(!finalCheckRequired)}
            />
          </Box>
        </Box>

        {/* Section 4: Package Details */}
        <Box sx={{ p: "22px 28px 20px", borderBottom: `1px solid ${DIVIDER}` }}>
          <Box sx={sectionLabelSx}>Package Details</Box>
          <Box sx={{ mb: "16px" }}>
            <ToggleCard
              label={t("packages.noTrackingYet.label")}
              sub={t("packages.noTrackingYet.sub")}
              checked={noTrackingYet}
              onChange={() => {
                setNoTrackingYet(!noTrackingYet);
                if (!noTrackingYet) setTracking("");
              }}
            />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "16px", mb: "16px" }}>
            <Box sx={{ position: "relative" }}>
              <FieldLabel htmlFor="tracking">TRACKING NUMBER</FieldLabel>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  border: `1.5px solid ${trackingError ? "#f3a8ab" : BORDER}`,
                  borderRadius: RADIUS_INPUT,
                  height: "50px",
                  overflow: "hidden",
                  opacity: noTrackingYet ? 0.5 : 1,
                  "&:hover": { borderColor: trackingError ? "#f3a8ab" : BORDER_PURPLE },
                }}
              >
                <Box
                  component="input"
                  id="tracking"
                  value={tracking}
                  required={!noTrackingYet}
                  disabled={noTrackingYet}
                  placeholder="e.g. LP00803636988128"
                  maxLength={TRACKING_MAX_LENGTH}
                  onChange={(e) => setTracking(getScanText(e.target.value))}
                  onKeyDown={(e) => {
                    if (e.keyCode === 13) {
                      setTracking(getScanText(tracking));
                      document.getElementById("shippingFee").select();
                      document.getElementById("shippingFee").focus();
                      e.preventDefault();
                    }
                  }}
                  sx={{
                    flex: 1,
                    height: "100%",
                    border: "none",
                    outline: "none",
                    padding: "0 14px",
                    fontSize: "13.5px",
                    fontWeight: 500,
                    color: trackingError ? "#c0343a" : TEXT_BODY,
                    background: "transparent",
                  }}
                />
                <IconButton
                  onClick={() => setOpen(true)}
                  disabled={noTrackingYet}
                  sx={{ width: 44, height: "100%", borderRadius: 0, background: BG_PURPLE_TINT, borderLeft: `1px solid ${BORDER}` }}
                >
                  <CameraAltIcon sx={{ fontSize: 18, color: PURPLE_DARK }} />
                </IconButton>
              </Box>
              {trackingError && (
                <Box sx={{ color: "#c0343a", fontSize: "0.75rem", mt: "4px" }}>
                  Tracking number must be at least 8 characters ({tracking.length}/8)
                </Box>
              )}
            </Box>

            <TextField
              label="Order ID"
              id="orderId"
              value={orderId}
              sx={fieldSx}
              onChange={(e) => setOrderId(e.target.value)}
            />
          </Box>

          <TextField
            fullWidth
            multiline
            maxRows={5}
            minRows={3}
            label="Description"
            id="description"
            value={description}
            sx={{ ...fieldSx, mb: "16px" }}
            onChange={(e) => setDescription(e.target.value)}
          />

          <TextField
            fullWidth
            multiline
            maxRows={5}
            label="Remark"
            id="remark"
            value={remark}
            sx={fieldSx}
            onChange={(e) => setRemark(e.target.value)}
          />
        </Box>

        {/* Section 5: Pricing */}
        <Box sx={{ p: "22px 28px 20px", borderBottom: `1px solid ${DIVIDER}` }}>
          <Box sx={sectionLabelSx}>Pricing</Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px" }}>
            <TextField
              type="number"
              label="Amount"
              id="amount"
              value={amount}
              sx={fieldSx}
              onChange={(e) => setAmount(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">฿</InputAdornment> }}
            />

            <Box sx={{ position: "relative" }}>
              <FieldLabel>QUANTITY</FieldLabel>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  border: `1.5px solid ${BORDER}`,
                  borderRadius: RADIUS_INPUT,
                  height: "50px",
                  overflow: "hidden",
                  "&:hover": { borderColor: BORDER_PURPLE },
                }}
              >
                <IconButton
                  onClick={() => setQuantity((q) => Math.max(1, Number(q || 1) - 1))}
                  sx={{ width: 44, height: "100%", borderRadius: 0, background: "#f8f7fa", borderRight: `1px solid ${BORDER}`, fontSize: 20, color: PURPLE_MAIN }}
                >
                  −
                </IconButton>
                <Box
                  component="input"
                  type="number"
                  id="quantity"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  sx={{
                    flex: 1,
                    textAlign: "center",
                    border: "none",
                    outline: "none",
                    fontFamily: MONO,
                    fontSize: "16px",
                    fontWeight: 700,
                    color: TEXT_BODY,
                    background: "transparent",
                    "&::-webkit-outer-spin-button": { WebkitAppearance: "none", margin: 0 },
                    "&::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
                    MozAppearance: "textfield",
                  }}
                />
                <IconButton
                  onClick={() => setQuantity((q) => Number(q || 0) + 1)}
                  sx={{ width: 44, height: "100%", borderRadius: 0, background: "#f8f7fa", borderLeft: `1px solid ${BORDER}`, fontSize: 20, color: PURPLE_MAIN }}
                >
                  +
                </IconButton>
              </Box>
            </Box>

            <TextField
              type="number"
              label="Shipping Fee"
              id="shippingFee"
              value={shippingFee}
              sx={accentFieldSx}
              onChange={(e) => setShippingFee(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start" sx={{ color: PURPLE_DARK }}>฿</InputAdornment> }}
            />
          </Box>
        </Box>

        {/* Footer: Actions */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "20px 28px", background: BG_TINT, gap: "14px", flexWrap: "wrap" }}>
          <Button variant="outlined" color="secondary" startIcon={<CloseIcon fontSize="small" />} onClick={() => clearForm()}>
            Clear Form
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<BookmarkBorderIcon fontSize="small" />}
              onClick={handleSaveDraft}
              sx={{ background: BG_PURPLE_TINT, borderColor: BORDER_PURPLE, color: PURPLE_DARK, "&:hover": { background: "#ede8fb", borderColor: BORDER_PURPLE } }}
            >
              Save Draft
            </Button>
            <Button
              variant="contained"
              color="secondary"
              type="submit"
              startIcon={<CheckIcon fontSize="small" />}
              sx={{
                background: `linear-gradient(135deg,#7c3aed,${PURPLE_DARK})`,
                boxShadow: "0 4px 16px -4px rgba(109,40,217,.55)",
                "&:hover": { background: `linear-gradient(135deg,#7c3aed,${PURPLE_DARK})`, opacity: 0.92 },
              }}
            >
              Submit
            </Button>
          </Box>
        </Box>

      </BentoCard>
      <Scanner
        open={open}
        setOpen={setOpen}
        onDecode={(result) => {
          if (result) {
            console.log(result);
            setTracking(() => getScanText(result));
            document.getElementById("shippingFee").select();
            document.getElementById("shippingFee").focus();
            setOpen(false);
          }
        }}
      />
    </form>
  );
};

export default NewPackage;
